import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Star, ArrowLeft, Bus, MapPin, Calendar, User,
  Send, CheckCircle
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const BusReview = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [booking, setBooking] = useState(null);
  
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [categories, setCategories] = useState({
    punctuality: 0,
    comfort: 0,
    cleanliness: 0,
    staff: 0
  });

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await api.get('/bus/bookings/my');
      const found = response.data.bookings?.find(b => b.id === bookingId);
      
      if (!found) {
        toast({
          title: 'Error',
          description: 'Booking not found',
          variant: 'destructive'
        });
        navigate('/bus/tickets');
        return;
      }
      
      if (found.reviewed) {
        toast({
          title: 'Already Reviewed',
          description: 'You have already reviewed this trip',
        });
        navigate('/bus/tickets');
        return;
      }
      
      setBooking(found);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load booking',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast({
        title: 'Rating Required',
        description: 'Please select an overall rating',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/bus/reviews', {
        booking_id: bookingId,
        rating: rating,
        review_text: reviewText || null,
        categories: Object.values(categories).some(v => v > 0) ? categories : null
      });
      
      setSubmitted(true);
      toast({
        title: 'Review Submitted!',
        description: 'Thank you for your feedback',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to submit review',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const setCategoryRating = (category, value) => {
    setCategories(prev => ({ ...prev, [category]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Thank You!</h1>
          <p className="text-gray-600 mb-6">
            Your review helps other travelers make better decisions and 
            helps operators improve their service.
          </p>
          <div className="flex gap-3">
            <Button 
              variant="outline"
              className="flex-1"
              onClick={() => navigate('/bus/tickets')}
            >
              View My Tickets
            </Button>
            <Button 
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              onClick={() => navigate('/bus')}
            >
              Book Another Trip
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/bus/tickets')}>
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-xl font-bold">Rate Your Trip</h1>
              <p className="text-blue-100 text-sm">Help others with your feedback</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        {/* Trip Info */}
        <Card className="p-4 mb-4">
          <div className="flex items-center gap-4">
            <Bus className="h-10 w-10 text-blue-600" />
            <div>
              <h3 className="font-semibold">{booking?.operatorName}</h3>
              <p className="text-sm text-gray-500">
                {booking?.boarding?.city} → {booking?.alighting?.city}
              </p>
              <p className="text-sm text-gray-500">
                {new Date(booking?.trip?.date).toLocaleDateString()}
              </p>
            </div>
          </div>
        </Card>

        {/* Review Form */}
        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Overall Rating */}
            <div className="text-center">
              <Label className="text-lg mb-3 block">Overall Rating *</Label>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1"
                  >
                    <Star 
                      className={`h-10 w-10 transition-all ${
                        star <= (hoverRating || rating)
                          ? 'text-yellow-400 fill-yellow-400 scale-110'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {rating === 1 && 'Poor'}
                {rating === 2 && 'Fair'}
                {rating === 3 && 'Good'}
                {rating === 4 && 'Very Good'}
                {rating === 5 && 'Excellent'}
              </p>
            </div>

            {/* Category Ratings */}
            <div className="border-t pt-6">
              <Label className="mb-4 block">Rate Specific Areas (Optional)</Label>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { key: 'punctuality', label: 'Punctuality' },
                  { key: 'comfort', label: 'Comfort' },
                  { key: 'cleanliness', label: 'Cleanliness' },
                  { key: 'staff', label: 'Staff/Service' }
                ].map(({ key, label }) => (
                  <div key={key} className="space-y-1">
                    <span className="text-sm text-gray-600">{label}</span>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setCategoryRating(key, star)}
                          className="p-0.5"
                        >
                          <Star 
                            className={`h-5 w-5 ${
                              star <= categories[key]
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Review Text */}
            <div>
              <Label>Your Review (Optional)</Label>
              <Textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="Tell others about your experience..."
                rows={4}
                className="mt-2"
              />
            </div>

            <Button 
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 py-6"
              disabled={submitting || rating === 0}
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Submit Review
                </>
              )}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default BusReview;
